# Route: /audit/entry/:auditId
# Read-only detail view for a single audit entry (the F75 audit grid's What
# cell navigates here). Shows the humanized summary plus every raw identifier
# and the tamper-evident hash — the single canonical home for per-entry
# integrity data (the /audit grid row stays legible).
# Audience: Quality, Supervisor, Admin (RequirePermission AuditLog:Read).
# Backend: GET /api/audit/entry/{auditId:guid} (AuditEndpoints.GetAuditEntry),
# fetched via the AuditQuery model's `get:` route.

page-audit-detail:
  type: Page
  archetype: detail
  meta:
    title: $t.audit_detail.meta_title
    route: /audit/entry/:auditId
    layout: shell
    auth: required
  data:
    entry:
      model: AuditQuery
      key: $route.auditId
      required: true
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - audit-detail-header
    - audit-detail-info
    - audit-detail-verify
    - audit-detail-signed-summary
    - audit-detail-values

# ── Header: back-to-list (outlined secondary, from back_route) + summary ────
# Read-only page: no lifecycle actions. The PageHeaderTransform renders the
# back_route as a secondary (outlined) "← Back" button ($t.common.btn_back).

audit-detail-header:
  type: PageHeader
  back_route: /audit
  bind:
    content: entry.summary

# ── Summary card: label/value grid per patterns.md § Detail page composition ─

# signature_surface: who acted, on what, when and why is the record an auditor reads; it is read
# as the platform rendered it, never through a tenant customization (Feature #1913).
audit-detail-info:
  type: Card
  signature_surface: true
  spatial:
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - audit-info-grid
    - audit-info-source-text-id-note

audit-info-grid:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - audit-info-summary-label
    - audit-info-summary-value
    - audit-info-when-label
    - audit-info-when-value
    - audit-info-recorded-label
    - audit-info-recorded-value
    - audit-info-who-label
    - audit-info-who-value
    - audit-info-user-id-label
    - audit-info-user-id-value
    - audit-info-action-label
    - audit-info-action-value
    - audit-info-entity-type-label
    - audit-info-entity-type-value
    - audit-info-entity-type-id-label
    - audit-info-entity-type-id-value
    - audit-info-entity-label-label
    - audit-info-entity-label-value
    - audit-info-entity-id-label
    - audit-info-entity-id-value
    - audit-info-reason-label
    - audit-info-reason-value
    - audit-info-reason-code-label
    - audit-info-reason-code-value
    - audit-info-reason-code-meaning-label
    - audit-info-reason-code-meaning-value
    - audit-info-reason-text-label
    - audit-info-reason-text-value
    - audit-info-reason-proposed-label
    - audit-info-reason-proposed-value
    - audit-info-reason-vocabulary-label
    - audit-info-reason-vocabulary-value
    - audit-info-reason-vocabulary-version-id-label
    - audit-info-reason-vocabulary-version-id-value
    - audit-info-correlation-id-label
    - audit-info-correlation-id-value
    - audit-info-audit-id-label
    - audit-info-audit-id-value

# THE ONE FACT ON THIS CARD THAT IS NOT ON A ROW. `tooltip_entity_type` and
# `tooltip_entity_id` carry it too, but a tooltip emits as a `title` attribute on a
# non-focusable <p> — hover only, so it reaches neither a keyboard user nor a touch
# one. The other tooltips on this card are supplementary: a reader who never sees
# `tooltip_when` still has the "When" and "When it was recorded" rows to tell the two
# instants apart. This one is not supplementary — nothing else on the page says that
# on an evaluated expression Entity ID holds SOURCE TEXT rather than a record id,
# and a reviewer who misses it reads a fragment of an expression as the thing that
# was acted upon. That is the misreading the tooltips were added to prevent, so the fact is
# stated visibly here and the tooltips stay as the per-row detail.
#
# Gated, because the note speaks of an Entity ID that holds source text and this page
# renders every kind of entry: on a lot-released, sign-off or tenant row it explains a
# convention that does not apply to what the reader is looking at, and a card that
# carries a paragraph about some OTHER kind of entry teaches the reader to skip it —
# including on the rows where it is the one fact not on a row. Gated on the
# server-computed `entityIdIsSourceText` rather than on the entry's type or action
# here: `AuditEntry.EntityIdIsSourceText` is the one predicate every reader shares,
# and AuditEndpoints.MapToDtosAsync projects it once, from the entry's action.
#
# The trail reads the same flag for a different job: `audit-row-source-text-id-badge` in
# `audit-trail.cir.yaml` marks the row whose ID is source text. Neither CIR use
# suppresses anything — the trail's "Affected" label is withheld SERVER-SIDE, by the
# `!entityIdIsSourceText` guard in AuditEndpoints. That guard is what does the work, so
# do not read either page's use of the flag as making it redundant and delete it — a
# badge says what the row is; it does not stop the label rendering executed source.
audit-info-source-text-id-note:
  type: Text
  content: $t.audit_detail.note_entity_id_is_source_text
  visible_when: entry.entityIdIsSourceText == true
  spatial:
    margin_block_start: $spacing.md
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-summary-label:
  type: Text
  content: $t.audit_detail.label_summary
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-summary-value:
  type: Text
  bind:
    content: entry.summary
  tokens:
    font_size: $font.size.md
    font_weight: "600"

# TWO TIMES, AND THE PAGE SAYS WHICH IS WHICH (FR-24). The label was "When",
# which was unambiguous only while the trail was written inline and the two
# instants were microseconds apart. The durable relay makes the lag bounded
# rather than negligible, so a reader who cannot tell the two apart reads the
# recording time as the moment of the fact. Both are inside the hash chain
# (V093), which is what makes the gap between them evidence rather than a
# display detail.
audit-info-when-label:
  type: Text
  content: $t.audit_detail.label_when
  tooltip: $t.audit_detail.tooltip_when
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-when-value:
  type: Text
  format: compliance-datetime
  bind:
    content: entry.timestamp
  tokens:
    font_size: $font.size.md
    font_weight: "600"

audit-info-recorded-label:
  type: Text
  content: $t.audit_detail.label_recorded
  tooltip: $t.audit_detail.tooltip_recorded
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-recorded-value:
  type: Text
  format: compliance-datetime
  bind:
    content: entry.recordedAt
  tokens:
    font_size: $font.size.md
    font_weight: "600"

audit-info-who-label:
  type: Text
  content: $t.audit_detail.label_who
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# Who — display name from the read-model, rendered as a link to the user's
# profile page (M1 manual test pass Act 1.5 — "why is there the user id? we
# may have a link to the user page"). The dynamic segment is embedded
# directly in `route:` (not a separate `params:` map) — confirmed via emit
# that a navigate action's `params:` only resolves `$route.<name>` (the
# CURRENT page's own path params); `$data.<alias>.<field>` there silently
# falls back to reading `_routeParams` by the target key name, which is
# wrong here (this page's only route param is `auditId`, not `userId`).
# Embedding `$data.entry.userId` straight into the route path (matching the
# pattern audit-trail.cir.yaml's own row-navigate action uses) resolves
# correctly. No enabled/visible_when guard for the rare system-initiated row
# with no userId — both are unwired for this node shape in the pinned XP85
# build (confirmed via emit: predicate silently dropped); the button just
# shows blank and links to `/admin/users/`, a harmless edge case, rather than
# risk a second (guarded) sibling node diverging from this one.
audit-info-who-value:
  type: Button
  variant: ghost
  bind:
    content: entry.userDisplayName
  a11y:
    label: $t.audit_detail.who_link_label
  action:
    type: navigate
    route: /admin/users/$data.entry.userId
  tokens:
    font_size: $font.size.md
    padding_inline: 0

audit-info-user-id-label:
  type: Text
  content: $t.audit_detail.label_user_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-user-id-value:
  type: Text
  bind:
    content: entry.userId
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

# "Action" is the raw system event name (e.g. "RolePermissionsUpdated") — kept
# for technical/forensic traceability, distinct from the humanized `summary`
# already shown at the top of this card. The label + tooltip say so explicitly
# so it doesn't read as a second, less-readable copy of the summary.
#
# M1 manual test pass (Act 1.5) found this row plus a since-removed "Action
# type" row (entry.actionType, e.g. "Updated") both restating the verb already
# embedded in the raw action name ("RolePermissionsUpdated" / "Role" /
# "update" all say "update") with no independent information. Action type is
# dropped from this grid — EntityType (below) already gives the "what kind of
# thing" answer, and Summary (above) gives the humanized verb; the raw system
# event name here is the one place the exact, unhumanized value is still
# available, for anyone who needs it verbatim.
audit-info-action-label:
  type: Text
  content: $t.audit_detail.label_action
  tooltip: $t.audit_detail.tooltip_action
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-action-value:
  type: Text
  bind:
    content: entry.action
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

# ENTITY TYPE / ENTITY ID ARE NOT ALWAYS THE ACTED-UPON RECORD. Measured on
# `DefaultAuditEntryFactory.CreateFromEvent`: all three production producers —
# `AuditingExpressionEvaluator`, `AuditingScriptEngine`, `AuditingDeviceScriptEngine`
# — construct their event with `EntityType: null, EntityId: null`, so the factory's
# fallbacks are the only case on this path, and they differ by event:
# - an `ExpressionEvaluated` entry reads `e.EntityType ?? "Expression"` and
#   `e.EntityId ?? ExtensionRecordFields.IndexedLabel(e.Expression)`: its Entity ID
#   is the first 200 characters of the expression's SOURCE TEXT (`IndexedLabel`
#   appends "..." when it cuts), never a record identifier;
# - a `ScriptExecuted` entry reads `?? "Script"`, and its Entity ID is always the
#   script's identity label, `<definition>@<version>#<function>` — every script
#   execution carries its identity — with the executed text in the hashed value.
#
# A reviewer investigating an expression entry is doing exactly the compliance
# workflow this feature exists for, and without a tooltip the page invites them
# to read a fragment of source as the thing that was acted on. The way back to
# the operation is the correlation ID, which is why the tooltip names it — the
# same job `tooltip_when`, `tooltip_recorded`, `tooltip_action` and
# `tooltip_correlation_id` already do for their own rows.
audit-info-entity-type-label:
  type: Text
  content: $t.audit_detail.label_entity_type
  tooltip: $t.audit_detail.tooltip_entity_type
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-entity-type-value:
  type: Text
  bind:
    content: entry.entityTypeLabel
  tokens:
    font_size: $font.size.md
    font_weight: "600"

# The entity type as it is RECORDED, beside the label above, which is a translation and so can
# read differently per language. This is the value the trail's Entity type filter matches and the
# one the hash chain covers, so it stays on the page verbatim, as the raw action does (#1557).
audit-info-entity-type-id-label:
  type: Text
  content: $t.audit_detail.label_entity_type_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-entity-type-id-value:
  type: Text
  bind:
    content: entry.entityType
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

# Affected/entityLabel is omitted (both label + value together, so the
# label/value pairing in this grid never drifts by one cell) rather than
# shown blank when the read-model has no label for this entity type (M1
# manual test pass Act 1.5 — Role rows rendered an empty "Affected" row).
# AuditEndpoints.MapToDtosAsync already falls back entityLabel to entityId
# for human-readable ids (Role names etc.); a null here means a genuinely
# opaque id with nothing informative to show.
audit-info-entity-label-label:
  type: Text
  content: $t.audit_detail.label_entity_label
  visible_when: entry.entityLabel != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-entity-label-value:
  type: Text
  bind:
    content: entry.entityLabel
  visible_when: entry.entityLabel != null
  tokens:
    font_size: $font.size.md
    font_weight: "600"

audit-info-entity-id-label:
  type: Text
  content: $t.audit_detail.label_entity_id
  tooltip: $t.audit_detail.tooltip_entity_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-entity-id-value:
  type: Text
  bind:
    content: entry.entityId
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

# The retired free-text reason, kept for entries written before reasons were structured.
# A new entry never writes it (DefaultAuditEntryFactory passes `Reason: null`), so both
# rows are gated: unguarded, every entry written since showed an empty "Reason" above the
# two structured rows, and an auditor read three reason-shaped labels with one always blank.
audit-info-reason-label:
  type: Text
  content: $t.audit_detail.label_reason_legacy
  visible_when: entry.reason != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-reason-value:
  type: Text
  bind:
    content: entry.reason
  visible_when: entry.reason != null
  tokens:
    font_size: $font.size.md
    font_weight: "600"

# The reason as it is captured today — a vocabulary code, its explanation, whether the
# operator proposed a code the vocabulary lacks, and the vocabulary version the code
# was checked against. All four are inside the hashed payload, so the chain vouches
# for them. Each row shows only when its entry carries the value: an entry written
# before reasons were structured carries the free-text reason above instead.
audit-info-reason-code-label:
  type: Text
  content: $t.audit_detail.label_reason_code
  visible_when: entry.reasonCode != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-reason-code-value:
  type: Text
  bind:
    content: entry.reasonCode
  visible_when: entry.reasonCode != null
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

# What the code meant under the version it is pinned to. The code above is what the chain
# hashed; this is what a person reading the trail needs, and without it an inspector had to
# ask an administrator to resolve EQP_DRIFT against a version that may since have redefined
# it (#1739).
#
# BOTH halves gate on the resolved label, for the reason the vocabulary pair below states:
# in a two-column Grid a caption that renders without its value shifts every later cell.
audit-info-reason-code-meaning-label:
  type: Text
  content: $t.audit_detail.label_reason_code_meaning
  tooltip: $t.audit_detail.tooltip_reason_code_meaning
  visible_when: entry.reasonCodeLabel != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-reason-code-meaning-value:
  type: Text
  bind:
    content: entry.reasonCodeLabel
  visible_when: entry.reasonCodeLabel != null
  tokens:
    font_size: $font.size.md

audit-info-reason-text-label:
  type: Text
  content: $t.audit_detail.label_reason_text
  visible_when: entry.reasonText != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-reason-text-value:
  type: Text
  bind:
    content: entry.reasonText
  visible_when: entry.reasonText != null
  tokens:
    font_size: $font.size.md

audit-info-reason-proposed-label:
  type: Text
  content: $t.audit_detail.label_reason_proposed
  visible_when: entry.reasonProposedNewCode == true
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-reason-proposed-value:
  type: Text
  content: $t.audit_detail.value_reason_proposed
  visible_when: entry.reasonProposedNewCode == true
  tokens:
    font_size: $font.size.md
    font_weight: "600"

audit-info-reason-vocabulary-label:
  type: Text
  content: $t.audit_detail.label_reason_vocabulary
  tooltip: $t.audit_detail.tooltip_reason_vocabulary
  visible_when: entry.reasonVocabularyLabel != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# The vocabulary the code was checked against, shown by NAME. The hashed payload carries
# its version uuid; the server resolves that to "<name> v<n>" the same way it resolves
# entityLabel.
#
# BOTH halves gate on the resolved label, which is the one thing this row exists to show.
# They used to gate on different predicates — the caption on the uuid, the value on the
# label — on the reasoning that a lookup returning nothing would leave no empty row. In a
# two-column Grid it does the opposite: the caption renders, the value does not, and every
# later cell shifts one column. Six other label/value pairs in this grid share one
# predicate; this is the seventh.
audit-info-reason-vocabulary-value:
  type: Text
  bind:
    content: entry.reasonVocabularyLabel
  visible_when: entry.reasonVocabularyLabel != null
  tokens:
    font_size: $font.size.md

# The uuid the hashed payload actually carries, shown raw beside the name resolved from it —
# the same treatment entity-label/entity-id get above, and for the same reason: an auditor
# reconciling this entry against the chain needs the value that was hashed, not only the name
# a later lookup produced for it. Gated where entity-id is not, because an entry that named no
# vocabulary has no uuid, whereas every entry has an entity id.
audit-info-reason-vocabulary-version-id-label:
  type: Text
  content: $t.audit_detail.label_reason_vocabulary_version_id
  tooltip: $t.audit_detail.tooltip_reason_vocabulary_version_id
  visible_when: entry.reasonVocabularyVersionId != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-reason-vocabulary-version-id-value:
  type: Text
  bind:
    content: entry.reasonVocabularyVersionId
  visible_when: entry.reasonVocabularyVersionId != null
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

audit-info-correlation-id-label:
  type: Text
  content: $t.audit_detail.label_correlation_id
  tooltip: $t.audit_detail.tooltip_correlation_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-correlation-id-value:
  type: Text
  bind:
    content: entry.correlationId
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

audit-info-audit-id-label:
  type: Text
  content: $t.audit_detail.label_audit_id
  tooltip: $t.audit_detail.tooltip_audit_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-info-audit-id-value:
  type: Text
  bind:
    content: entry.auditId
  tokens:
    font_size: $font.size.md
    font_family: $font.family.mono
    font_weight: "600"

# ── Verify-integrity card: the tamper-evident hash ──────────────────────────

# signature_surface: the hash a reviewer verifies the record's integrity against is read as
# the platform rendered it, never through a tenant customization (Feature #1913).
audit-detail-verify:
  type: Card
  signature_surface: true
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - audit-verify-title
    - audit-verify-hash-row

audit-verify-title:
  type: Text
  content: $t.audit_detail.verify_title
  tokens:
    font_size: $font.size.md
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

audit-verify-hash-row:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
    align: baseline
    wrap: wrap
  children:
    - audit-verify-hash-label
    - audit-verify-hash-value

audit-verify-hash-label:
  type: Text
  content: $t.audit_detail.label_hash
  tokens:
    font_size: $font.size.sm
    font_weight: "600"
    color: $color_scheme.light.color.on-surface-muted

audit-verify-hash-value:
  type: Text
  bind:
    content: entry.hash
  tokens:
    font_size: $font.size.sm
    font_family: $font.family.mono
    color: $color_scheme.light.color.on-surface
    font_weight: "600"

# ── Sign-off summary: "what was signed" (M1 audit-trail-ux-hardening) ───────
# Compact summary of the signed TrackingRecord, attached only to a sign-off row
# (AuditEndpoints.GetAuditEntry populates entry.signedEntry* only when
# entityType == TrackingRecord and actionType == Signed). M1 manual test pass
# Act 6.3 — "it's impossible to understand what was signed" — the audit row
# alone gave no way to see the entry's template, context, or narrative
# without navigating away from the audit trail.

# signature_surface: what a signature was applied to is read as the platform rendered it,
# never through a tenant customization (Feature #1913).
audit-detail-signed-summary:
  type: Card
  signature_surface: true
  visible_when: entry.signedEntryFormName != null
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - audit-signed-summary-title
    - audit-signed-summary-grid

audit-signed-summary-title:
  type: Text
  content: $t.audit_detail.signed_summary_title
  tokens:
    font_size: $font.size.md
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

audit-signed-summary-grid:
  type: Grid
  spatial:
    columns: label-value
    gap: $spacing.md
    responsive:
      sm:
        columns: 1
  children:
    - audit-signed-summary-template-label
    - audit-signed-summary-template-value
    - audit-signed-summary-context-label
    - audit-signed-summary-context-value
    - audit-signed-summary-narrative-label
    - audit-signed-summary-narrative-value

audit-signed-summary-template-label:
  type: Text
  content: $t.audit_detail.signed_summary_label_template
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-signed-summary-template-value:
  type: Text
  bind:
    content: entry.signedEntryFormName
  tokens:
    font_size: $font.size.md
    font_weight: "600"

# Context (e.g. the asset the entry was recorded against) is optional on the
# entry itself — omit the row (label + value together) rather than show it
# blank when the entry carries no context.
audit-signed-summary-context-label:
  type: Text
  content: $t.audit_detail.signed_summary_label_context
  visible_when: entry.signedEntryContextDisplayName != null
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-signed-summary-context-value:
  type: Text
  bind:
    content: entry.signedEntryContextDisplayName
  visible_when: entry.signedEntryContextDisplayName != null
  tokens:
    font_size: $font.size.md
    font_weight: "600"

audit-signed-summary-narrative-label:
  type: Text
  content: $t.audit_detail.signed_summary_label_narrative
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

audit-signed-summary-narrative-value:
  type: Text
  bind:
    content: entry.signedEntryNarrativeExcerpt
  tokens:
    font_size: $font.size.md

# ── Value changes: full YAML diff for config-entity rows ────────────────────
# Before/After card for any audit row whose OldValue/NewValue the store
# already captured (Xp87.Audit.AuditEntry) — for M1 that is the
# AssetClassUpdated / TrackingFormUpdated "ReplaceFromYaml"/"StructureUpdated"
# editor-save events (DefaultAuditEntryFactory), though the card renders
# generically for any event carrying a captured value (e.g. RolePermissionsUpdated's
# permission snapshot, RoleDeleted's old-state snapshot) — a broader, still-
# honest generalisation of the M1 ask. Rendered as plain monospace before/
# after blocks rather than a computed line-diff — a display change over the
# already-captured text, not a new diff algorithm. M1 manual test pass Act
# 2.2 — "do we have the full yaml file? so it could be easy in the audit
# trail to see the full yaml from the audit record".
#
# Gated on the server-computed `hasValueChanges` flag rather than
# `entry.oldValue != null || entry.newValue != null` directly — confirmed via
# emit that the XP85 `visible_when` expression parser does not support
# compound `||`/`&&` predicates (it silently mis-parses everything after the
# first comparison as part of a string literal). AuditEndpoints.MapToDtosAsync
# computes the OR server-side once; the CIR side only ever compares one flag.
audit-detail-values:
  type: Card
  visible_when: entry.hasValueChanges == true
  spatial:
    padding: $spacing.lg
    display: flex
    direction: column
    gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
    radius: $radius.md
  children:
    - audit-values-title
    - audit-values-old-value
    - audit-values-new-value

audit-values-title:
  type: Text
  content: $t.audit_detail.values_title
  tokens:
    font_size: $font.size.md
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

# Rendered as a textarea (not a formatted Text block) — confirmed via emit
# that `format: pre` references a `formatPre` helper that does not exist in
# xp85-format-utils.js (a hard module-load error in the browser) and that
# neither `tokens: white_space: pre-wrap` nor `readonly:`/`read_only:` do
# anything (tokens: is a closed set of known design tokens, emits an unused
# CSS custom property for anything else; Input readonly is unwired for this
# node type in the pinned XP85 build). A textarea is the one node whose
# native rendering preserves whitespace/newlines with no CSS trick needed —
# `label:` gives it the same visible "Before" caption a plain Text row would
# have carried, so no separate label node is needed. The page has no Form /
# save path at all (archetype: detail, read-only page), so the field being
# technically editable in the DOM carries no real risk — and leaves the text
# selectable/copyable, which a disabled field would block.
#
# Create events have no prior state — omit the "Before" field entirely
# rather than show it empty.
audit-values-old-value:
  type: Input
  input_type: textarea
  label: $t.audit_detail.values_label_before
  bind:
    value: entry.oldValue
  visible_when: entry.oldValue != null
  a11y:
    label: $t.audit_detail.values_label_before
  tokens:
    font_size: $font.size.sm
    font_family: $font.family.mono

# Delete/deprecate-style events have no new state — omit the "After" field
# entirely rather than show it empty.
audit-values-new-value:
  type: Input
  input_type: textarea
  label: $t.audit_detail.values_label_after
  bind:
    value: entry.newValue
  visible_when: entry.newValue != null
  a11y:
    label: $t.audit_detail.values_label_after
  tokens:
    font_size: $font.size.sm
    font_family: $font.family.mono
